import React, { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DocumentModal = ({ isOpen, onClose, base64Data, fileName, fileType }) => {
    const [fileUrl, setFileUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'download'

    useEffect(() => {
        if (!isOpen || !base64Data) {
            setFileUrl(null);
            setViewMode('preview');
            setNumPages(null);
            return;
        }

        try {
            setError(null);
            const nameLower = fileName ? fileName.toLowerCase() : "";
            
            // 1. Clean and Convert Base64 String
            const base64Part = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
            const cleanBase64 = base64Part.replace(/[^A-Za-z0-9+/=]/g, "");
            const binaryString = window.atob(cleanBase64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // 2. Format Logic
            // Previewable: PDF and standard Images
            const isPdf = nameLower.endsWith('.pdf');
            const isImage = fileType && (
                fileType.includes('image/png') || 
                fileType.includes('image/jpeg') || 
                fileType.includes('image/jpg') || 
                fileType.includes('image/bmp') || 
                fileType.includes('image/tiff')
            );

            // 3. Create the Data Blob
            const mime = isPdf ? 'application/pdf' : (fileType || 'application/octet-stream');
            const blob = new Blob([bytes], { type: mime });
            const url = URL.createObjectURL(blob);
            setFileUrl(url);

            // 4. Set View Mode
            if (isPdf || isImage) {
                setViewMode('preview');
            } else {
                // DOCX, XLSX, PPTX, etc., will trigger the download UI
                setViewMode('download');
            }

        } catch (e) {
            console.error("Processing error:", e);
            setError("The file data could not be processed correctly.");
        }

        // Cleanup the object URL when modal closes to prevent memory leaks
        return () => { 
            if (fileUrl) URL.revokeObjectURL(fileUrl); 
        };
    }, [isOpen, base64Data, fileName, fileType]);

    if (!isOpen) return null;

    const isPdf = fileName && fileName.toLowerCase().endsWith('.pdf');
    const isImage = fileType && fileType.startsWith('image/');
    const canPreview = isPdf || isImage;
    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                
                {/* HEADER */}
                <div style={headerStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong style={{ fontSize: '14px', color: '#333' }}>{fileName}</strong>
                    </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginLeft: '20px' }}>
                    {canPreview && fileUrl && (
                        <a 
                            href={fileUrl} 
                            download={fileName} 
                            style={headerDownloadStyle}
                            title="Download File"
                        >
                            <span style={{ marginRight: '5px' }}></span> Download
                        </a>
                    )}
                    
                    <button onClick={onClose} style={closeBtnStyle} title="Close Preview">
                        &times;
                    </button>
    </div>
                </div>

                {/* MODAL BODY */}
                <div style={bodyStyle}>
                    {error ? (
                        <div style={messageBoxStyle}>
                            <p style={{ color: '#ff6b6b' }}>{error}</p>
                            <button onClick={onClose} style={downloadButtonStyle}>Close</button>
                        </div>
                    ) : viewMode === 'download' ? (
                        /* DOWNLOAD UI FOR DOCX/XLSX/PPTX */
                        <div style={messageBoxStyle}>
                            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📄</div>
                            <h2 style={{ margin: '0 0 10px 0', color: '#fff' }}>No Preview Available</h2>
                            <p style={{ margin: '0 0 25px 0', color: '#bbb', maxWidth: '300px', lineHeight: '1.5' }}>
                                This file type (e.g. Word, Excel) must be downloaded to be viewed on your device.
                            </p>
                            <a href={fileUrl} download={fileName} style={downloadButtonStyle}>
                                Download {fileName.split('.').pop().toUpperCase()} File
                            </a>
                        </div>
                    ) : isImage ? (
                        /* IMAGE PREVIEW */
                        <img 
                            src={fileUrl} 
                            alt="preview" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '70vh', // Keeps the modal from getting too tall for images
                                height: 'auto',
                                objectFit: 'contain' 
                            }} 
                        />
                    ) : (
                        /* PDF PREVIEW (using react-pdf v4.2.0 features) */
                        <Document
                            file={fileUrl}
                            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                            loading={<div style={{ color: '#fff' }}>Loading PDF Document...</div>}
                            error={<div style={{ color: '#fff' }}>Failed to render PDF. Please download instead.</div>}
                        >
                            {Array.from(new Array(numPages), (el, index) => (
                                <div key={index} style={pageContainerStyle}>
                                    <Page 
                                        pageNumber={index + 1} 
                                        width={Math.min(window.innerWidth * 0.8, 800)}
                                        renderTextLayer={false} 
                                        renderAnnotationLayer={false} 
                                    />
                                </div>
                            ))}
                        </Document>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- INLINE STYLES (Legacy Compatible) ---
const overlayStyle = {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex',
    justifyContent: 'center', alignItems: 'center'
};

const modalStyle = {
    // Limits the modal's growth, but allows it to be smaller
    maxWidth: '90%', 
    maxHeight: '90%', 
    
    // Centers the modal if it's smaller than the screen
    margin: 'auto', 
    
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
};

const headerStyle = {
    padding: '12px 20px', borderBottom: '1px solid #ddd',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fdfdfd'
};

const bodyStyle = {
    flex: 1,
    backgroundColor: '#525659',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    
    // This is the key: it allows the body to define its own height 
    // based on what's inside, up to the max-height of the modal.
    minHeight: '200px', 
    width: 'auto' 
};

const closeBtnStyle = {
    border: 'none', background: 'none', fontSize: '32px',
    cursor: 'pointer', color: '#999', lineHeight: '1'
};

const messageBoxStyle = {
    textAlign: 'center', color: '#fff', marginTop: '5%',
    padding: '50px', backgroundColor: '#3a3d40', borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)', display: 'flex',
    flexDirection: 'column', alignItems: 'center'
};

const downloadButtonStyle = {
    display: 'inline-block', padding: '14px 28px', backgroundColor: '#28a745',
    color: '#fff', borderRadius: '5px', textDecoration: 'none',
    fontWeight: 'bold', fontSize: '14px', border: 'none', cursor: 'pointer'
};

const pageContainerStyle = {
    marginBottom: '25px', boxShadow: '0 0 15px rgba(0,0,0,0.5)',
    backgroundColor: '#fff'
};
const headerDownloadStyle = {
    display: 'flex', alignItems: 'center', padding: '6px 12px',
    backgroundColor: '#28a745', color: '#fff', borderRadius: '4px',
    textDecoration: 'none', fontSize: '12px', fontWeight: 'bold',
    transition: 'background-color 0.2s'
};

export default DocumentModal;